import { formatVehicleNumberDisplay } from "../../../utils/vehicleNumberUtils";
import { confirmationStatusLabel, getInventoryStatus } from "./confirmationListUtils";

export function formatReportedAt(value, short = false) {
  if (!value) return "—";
  try {
    const d = new Date(value);
    if (short) {
      return d.toLocaleString(undefined, {
        day: "numeric",
        month: "short",
        hour: "2-digit",
        minute: "2-digit",
      });
    }
    return d.toLocaleString();
  } catch {
    return "—";
  }
}

export function statusBadgeClass(status) {
  const normalized = String(status || "").toUpperCase();
  if (normalized === "PENDING") return "cf-badge cf-badge--pending";
  if (normalized === "CONFIRMED") return "cf-badge cf-badge--confirmed";
  if (normalized === "REJECTED") return "cf-badge cf-badge--rejected";
  return "cf-badge";
}

export function showInventoryColumn(listFilter) {
  return String(listFilter || "").toUpperCase() === "CONFIRMED";
}

export function showPaymentColumns(listView) {
  return String(listView || "").toLowerCase() === "inventory-confirmed";
}

export function formatBillAmount(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  return `₹${n.toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

/** Field note: latest inventory-update note, else tracer + admin review notes. */
export function fieldNoteText(item) {
  const status = String(item?.status || "").toUpperCase();
  const revision = String(item?.inventoryRevisionNote || "").trim();
  if (revision && status !== "REJECTED") return revision;

  const tracer = String(
    item?.requestNote || item?.note || item?.fieldNotes || ""
  ).trim();
  const admin = String(item?.reviewNote || "").trim();
  if (tracer && admin) {
    if (
      tracer.toLowerCase() === admin.toLowerCase() ||
      tracer.toLowerCase().includes(admin.toLowerCase())
    ) {
      return tracer;
    }
    return `${tracer} · ${admin}`;
  }
  return tracer || admin || "—";
}

/** "What is loaded" text saved on the vehicle / case. */
export function loadedDetailsText(item) {
  const text = String(item?.loadedDetail || item?.loadedShort || "").trim();
  return text || "—";
}

export function actionLabel(item, isRepoAdmin) {
  const status = String(item?.status || "").toUpperCase();
  if (isRepoAdmin) {
    if (status === "PENDING") return "Review";
    const inv = getInventoryStatus(item);
    if (inv.key === "pending-upload") return "Upload Inventory";
    if (inv.key === "revision") return "Update Inventory";
    return "View";
  }
  if (status === "CONFIRMED") {
    const inv = getInventoryStatus(item);
    if (inv.key === "pending-upload") return "Upload Inventory";
    if (inv.key === "revision") return "Update Inventory";
    return "View";
  }
  return "—";
}

export function isRowClickable(item, isRepoAdmin) {
  const status = String(item?.status || "").toUpperCase();
  return isRepoAdmin || status === "CONFIRMED";
}

export function rowClassName(item, isRepoAdmin) {
  const status = String(item?.status || "").toUpperCase();
  const needsInventory =
    !isRepoAdmin && status === "CONFIRMED" && !item.inventorySubmitted;
  return [
    status === "PENDING" ? "confirmation-row-pending" : "",
    isRowClickable(item, isRepoAdmin) ? "confirmation-row-clickable" : "",
    needsInventory ? "confirmation-row-needs-inventory" : "",
  ]
    .filter(Boolean)
    .join(" ");
}

/** Label/value rows for mobile Excel stack (one case). */
export function buildMobileExcelRows(item, { isRepoAdmin, showInventoryCol, showPaymentCols }) {
  const rows = [
    {
      label: "Registration Number",
      value: formatVehicleNumberDisplay(item.vehicleNumber) || "—",
    },
    { label: "Customer Name", value: item.customerName || "—" },
    ...(isRepoAdmin
      ? [
          {
            label: "Bank / NBFC Branch",
            value: [item.bankName, item.branchName].filter(Boolean).join(" / ") || "—",
          },
        ]
      : []),
    { label: "Status", value: confirmationStatusLabel(item.status), isStatus: true },
  ];

  if (isRepoAdmin) {
    rows.push(
      { label: "Traced By", value: item.requestedByName || "—" },
      {
        label: "Role",
        value: item.requestedByRoleLabel || item.requestedByRole || "—",
      },
      { label: "Reporter Mobile", value: item.requestedByPhone || "—" }
    );
  }

  if (showInventoryCol) {
    rows.push({
      label: "Confirmed By",
      value: item.reviewedByName || "—",
    });
  }

  rows.push(
    { label: "Field Note", value: fieldNoteText(item) },
    { label: "Loaded details", value: loadedDetailsText(item) },
    { label: "Reported At", value: formatReportedAt(item.createdAt, true) }
  );

  if (showInventoryCol) {
    const inv = getInventoryStatus(item);
    rows.push({
      label: "Inventory",
      value: inv.key === "na" ? "—" : inv.label,
      inventory: inv,
    });
  }

  if (showPaymentCols) {
    rows.push(
      { label: "Vehicle Bill Amount", value: formatBillAmount(item.vehicleBillAmount) },
      { label: "Other Bill Amount", value: formatBillAmount(item.otherBillAmount) },
      { label: "Total Amount", value: formatBillAmount(item.totalAmount) }
    );
  }

  return rows;
}
